import type { ReactNode } from 'react'

/**
 * Every rung's action control. The height is fixed rather than minimum,
 * because "Create free account" wraps to two lines in a phone-width column
 * while "Subscribe" never does — and a button 18 px fatter than its three
 * neighbours reads as a mistake. Two lines of `leading-tight` text fit inside
 * 44 px, which is also the smallest comfortable touch target.
 */
export const TIER_ACTION =
  'flex w-full items-center justify-center h-11 px-3 rounded-lg text-xs font-medium leading-tight'

/** The line under the action. Its height is reserved on every card, empty or
 * not, so the buttons above it sit on one row.
 *
 * The reserve breaks where the grid does, because that is what changes the
 * column width: `md:grid-cols-4` gives 198 px of text, two columns below it
 * give 139 px. Measured at both, across all 20 interface languages — the
 * longest steady line is `pricing.switchHint`, which takes 2 lines at 198 px
 * everywhere and 3 at 139 px in ten languages (ru, de, fr, it, pt, nl, uz,
 * hu, id, ja). Reserve less and those ten carry their button 14 px above
 * their neighbours'; reserve more and every card grows a dead line.
 *
 * `pricing.signInFirst` runs to 4 lines and is deliberately NOT reserved
 * for: it shows for the ~150 ms between pressing Subscribe and Auth0 taking
 * over, and paying for it permanently is the worse trade. */
export const TIER_HINT = 'mt-1.5 min-h-[3.75em] md:min-h-[2.5em]'

interface TierCardProps {
  name: string
  /** "$0", "$2", "$9.99" — a literal, like every other price on this page */
  price: string
  /** "/month". The free tier has no billing period, so it has none. */
  priceNote?: string
  /** The one thing that actually differs between the four tiers, which is
   * why it is the biggest element in the card */
  count: number
  unit: string
  /** Terms of payment, not a feature: "Cancel anytime", "No card needed",
   * "2 left · back on 1 September" */
  bill?: string
  badge?: { text: string; tone: 'primary' | 'current' }
  highlighted?: boolean
  /** The reader's own tier. Amber, not the accent blue: blue is the colour
   * of every buy button on this page, and the one card you cannot buy should
   * not wear it. It is the same marker the subscribed plan has always used. */
  own?: boolean
  /** The action area. Empty is a legitimate state: a subscriber has nothing
   * to press on the free rung. */
  children?: ReactNode
  /** The line under the action — "cancel your current plan first", the
   * renewal date. It belongs to the card rather than to the action so the
   * space below every button is the same height; see the render. */
  hint?: ReactNode
}

/**
 * One rung of the pricing ladder.
 *
 * The four tiers are the same product in different amounts — there is no
 * feature any of them lacks — so the feature list lives once, above the grid,
 * and the card is left holding the only thing that varies. Repeated four
 * times under four cards it was 24 checkmarks saying six things, and the
 * number that separates the tiers was the smallest text in the box.
 */
export function TierCard({
  name,
  price,
  priceNote,
  count,
  unit,
  bill,
  badge,
  highlighted,
  own,
  children,
  hint,
}: TierCardProps) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-4 text-center transition-all duration-200 ${
        own
          ? 'border-warning bg-surface shadow-lg shadow-warning/10'
          : highlighted
            ? 'border-primary bg-surface shadow-lg shadow-primary/10'
            : 'border-border bg-surface hover:border-text-muted hover:shadow-md'
      }`}
    >
      {badge && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-xs font-semibold rounded-full ${
            badge.tone === 'primary' ? 'bg-primary text-on-accent' : 'bg-warning text-on-warning'
          }`}
        >
          {badge.text}
        </div>
      )}

      <h3 className="text-sm font-semibold text-text">{name}</h3>

      <div className="mt-1.5 flex items-baseline justify-center gap-0.5">
        <span className="text-2xl font-bold text-text">{price}</span>
        {priceNote && <span className="text-xs text-text-muted">{priceNote}</span>}
      </div>

      <div className="mt-3 text-3xl font-bold leading-none text-primary tabular-nums">{count}</div>
      <div className="mt-3 text-xs text-text-muted leading-tight">{unit}</div>

      {/* Reserved whether or not there is a line to put in it — the four cards
          keep their buttons on one row */}
      <p className="mt-2 min-h-[2.6em] text-[11px] leading-tight text-text-muted">{bill}</p>

      {/* The action block is bottom-anchored, so the room under the button
          decides where the button lands. Reserving the hint's height on every
          card — the way `bill` above is reserved — is what puts all four
          buttons on one line: without it a card with a two-line hint carried
          its button 28 px higher than its neighbours. */}
      <div className="mt-auto pt-2">
        {children}
        <p className={`${TIER_HINT} text-[11px] leading-tight text-text-muted`}>{hint}</p>
      </div>
    </div>
  )
}
